// craftJudgeClient.js — PART A (renderer): run the live craft judge for a run.
//
// The Worker has no local LLM, so the renderer drives the v1 prepare/complete
// pattern: ask the Worker for the aggregate-only judge prompt, call the local
// model (Ollama / LM Studio via the Electron bridge), and hand the output back
// for the Worker to parse + apply. Best-effort: no local model → no-op (proxy
// scores stand). THE LAW: the judge refines scores only, never approves.

import { apiFetch } from '../api.js';

/** Resolve a local-LLM engine from the Electron bridge, or null if none. */
export function resolveDefaultEngine() {
  const api = globalThis.window?.apolo;
  if (api?.ollama?.chat) return { chat: (p) => api.ollama.chat(p), baseUrl: 'http://localhost:11434/v1', model: 'qwen2.5:latest' };
  if (api?.lmstudio?.chat) return { chat: (p) => api.lmstudio.chat(p), baseUrl: 'http://localhost:1234/v1', model: 'google/gemma-4-e4b' };
  return null;
}

/**
 * Refine a run's craft scores with the local LLM. Returns a status object; never
 * throws. `engine` + `fetchImpl` are injectable for tests.
 *
 * @param {{ runId: string, fetchImpl?: typeof apiFetch,
 *           engine?: { chat: Function, model?: string, baseUrl?: string } }} opts
 */
export async function runLocalCraftJudge({ runId, fetchImpl = apiFetch, engine } = {}) {
  if (!runId) return { ran: false, reason: 'no-runId' };
  const eng = engine || resolveDefaultEngine();
  if (!eng?.chat) return { ran: false, reason: 'no-local-llm' };

  const prep = await fetchImpl(`/api/evaluator/craft-judge/${encodeURIComponent(runId)}/prepare`, { method: 'POST' });
  if (!prep?.ok || !prep.data?.system) return { ran: false, reason: 'prepare-failed', error: prep?.error };

  let modelOutput = '';
  try {
    const r = await eng.chat({
      baseUrl: eng.baseUrl,
      model: eng.model,
      messages: [
        { role: 'system', content: prep.data.system },
        { role: 'user', content: prep.data.user },
      ],
      options: { temperature: 0.2 },
    });
    if (!r?.ok) return { ran: false, reason: 'llm-failed', error: r?.error };
    modelOutput = r.content || r.reasoning || '';
  } catch (e) {
    return { ran: false, reason: 'llm-threw', error: e?.message || String(e) };
  }

  const done = await fetchImpl(`/api/evaluator/craft-judge/${encodeURIComponent(runId)}/complete`, {
    method: 'POST',
    body: { model_output: modelOutput },
  });
  if (!done?.ok) return { ran: false, reason: 'complete-failed', error: done?.error };
  return { ran: true, updated: done?.data?.updated ?? 0, source: done?.data?.source ?? null };
}
